'use client'

type FilterToggle = {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}

type ProductTypeOption = {
  id: string
  label: string
}

export type FilterPanelProps = {
  categoryName: string
  totalProducts: number
  sort: string
  onSortChange: (value: string) => void
  isUpdating: boolean
  productTypeLabel: string
  productTypeOptions?: ProductTypeOption[]
  selectedProductType?: string
  onProductTypeChange?: (id: string) => void
  toggles?: FilterToggle[]
}

const FilterPanel = ({
  categoryName,
  totalProducts,
  sort,
  onSortChange,
  isUpdating,
  productTypeLabel,
  productTypeOptions = [],
  selectedProductType,
  onProductTypeChange,
  toggles = [],
}: FilterPanelProps) => {
  const formattedTotalProducts = new Intl.NumberFormat('en-US').format(
    totalProducts
  )

  return (
    <div className="rounded bg-[#fafafa] p-3">
      <h2 className="border-b pb-2 text-xl font-bold">Filter {categoryName}</h2>
      <p className="pt-2 text-sm italic text-gray-500">
        {isUpdating ? (
          <span className="flex items-center gap-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                strokeOpacity="0.25"
                strokeWidth="4"
              />
              <path
                d="M4 12a8 8 0 018-8"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
            Updating products...
          </span>
        ) : (
          <>
            {formattedTotalProducts} {productTypeLabel}
          </>
        )}
      </p>

      <div className="mb-3 mt-2">
        <label htmlFor="sale-sort" className="font-bold">
          Sort products:
        </label>
        <select
          id="sale-sort"
          name="sale-sort"
          value={sort}
          onChange={event => onSortChange(event.target.value)}
          className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1 focus:border-blue-500 focus:outline-none"
        >
          <option value="newest">Newest</option>
          <option value="a-to-z">A to Z</option>
          <option value="price-asc">Price (low to high)</option>
          <option value="price-desc">Price (high to low)</option>
          <option value="biggest-discount">Biggest discount</option>
          <option value="best-sellers">Best sellers</option>
          <option value="rating">Customer rating</option>
        </select>
      </div>

      {productTypeOptions.length > 0 && onProductTypeChange && (
        <div className="mb-3 mt-2">
          <strong className="font-bold">Filter by product type:</strong>
          <div className="mt-2 space-y-1">
            {productTypeOptions.map(option => (
              <div key={option.id} className="flex items-center">
                <input
                  type="radio"
                  id={`sale-product-type-${option.id}`}
                  name="sale-product-type"
                  checked={option.id === selectedProductType}
                  onChange={() => onProductTypeChange(option.id)}
                  className="form-radio text-vi-red"
                />
                <label
                  htmlFor={`sale-product-type-${option.id}`}
                  className="ml-2"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      {toggles.length > 0 && <hr className="my-4" />}

      {toggles.map(toggle => (
        <div key={toggle.id} className="mt-1 flex items-center">
          <input
            type="checkbox"
            id={`sale-toggle-${toggle.id}`}
            checked={toggle.checked}
            onChange={event => toggle.onChange(event.target.checked)}
            className="form-checkbox h-4 w-4 text-vi-red"
          />
          <label htmlFor={`sale-toggle-${toggle.id}`} className="ml-2">
            {toggle.label}
          </label>
        </div>
      ))}
    </div>
  )
}

export default FilterPanel
